import { format } from "date-fns";
import certificateBackground from "@/assets/certificate-background.jpg";
import eabLogoHorizontal from "@/assets/eab-logo-horizontal.png";

interface CertificateData {
  userName: string;
  courseName: string;
  certificateNumber: string;
  issuedAt: string;
  instructorName?: string;
}

/**
 * Escapes text before it is interpolated into the certificate HTML, which is
 * written into a new window via document.write(). Names and course titles are
 * user/admin-authored, so unescaped values could inject markup or script.
 */
function escapeHtml(value: string): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function generateCertificateHTML(data: CertificateData): string {
  const issueDate = format(new Date(data.issuedAt), "MMMM d, yyyy");
  const instructorName = escapeHtml(data.instructorName || "Course Instructor");

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Certificate - ${escapeHtml(data.courseName)}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Tangerine:wght@700&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap');
        
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          font-family: 'Open Sans', sans-serif;
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          background: #f5f5f5;
          padding: 20px;
        }
        .certificate {
          width: 1100px;
          height: 780px;
          position: relative;
          background-image: url('${certificateBackground}');
          background-size: cover;
          background-position: center;
          box-shadow: 0 10px 40px rgba(0,0,0,0.15);
        }
        .inner-box {
          position: absolute;
          top: 30px;
          left: 40px;
          right: 40px;
          bottom: 30px;
          background: rgba(255, 255, 255, 0.9);
          background-image: repeating-linear-gradient(
            0deg,
            transparent,
            transparent 6px,
            rgba(180, 180, 180, 0.08) 6px,
            rgba(180, 180, 180, 0.08) 7px
          );
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          padding: 25px 40px 20px;
          padding-top: 20px;
          text-align: center;
        }
        .logo-section {
          margin-bottom: 80px;
        }
        .logo {
          width: 67.5%;
          height: auto;
        }
        .certificate-title {
          font-size: 48px;
          font-weight: 400;
          color: #1C61E0;
          letter-spacing: 4px;
          text-transform: uppercase;
          margin-bottom: 4px;
        }
        .of-completion {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          color: #666;
          font-size: 13px;
          letter-spacing: 2px;
          text-transform: uppercase;
          margin-top: 0;
          margin-bottom: 24px;
        }
        .of-completion::before,
        .of-completion::after {
          content: '';
          width: 50px;
          height: 2px;
          background: linear-gradient(90deg, #1C61E0, #8FCA3F);
        }
        .presented-text {
          color: #555;
          font-size: 13px;
          letter-spacing: 4px;
          text-transform: uppercase;
          margin-bottom: 16px;
        }
        .recipient-name {
          font-family: 'Tangerine', cursive;
          font-size: 72px;
          font-weight: 700;
          color: #1C61E0;
          margin-bottom: 12px;
          line-height: 1;
        }
        .course-text {
          color: #555;
          font-size: 15px;
          margin-bottom: 0;
        }
        .course-name {
          font-weight: 600;
          color: #333;
        }
        .instructor-section {
          text-align: center;
          margin-top: 20px;
        }
        .instructor-name {
          font-weight: 700;
          color: #333;
          font-size: 15px;
          border-top: 2px solid #1C61E0;
          padding-top: 6px;
          display: inline-block;
          min-width: 160px;
        }
        .instructor-label {
          color: #666;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-top: 3px;
        }
        .details-section {
          text-align: center;
          margin-top: 15px;
        }
        .detail-row {
          font-size: 11px;
          color: #555;
          margin-bottom: 3px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .detail-value {
          font-weight: 600;
        }
        @media print {
          body { background: white; padding: 0; }
          .certificate { box-shadow: none; }
        }
      </style>
    </head>
    <body>
      <div class="certificate">
        <div class="inner-box">
          <div class="logo-section">
            <img src="${eabLogoHorizontal}" alt="Entrepreneurs Across Borders" class="logo" />
          </div>
          
          <h1 class="certificate-title">Certificate</h1>
          <div class="of-completion">of Completion</div>
          
          <p class="presented-text">We Proudly Present This Certificate To</p>
          
          <h2 class="recipient-name">${escapeHtml(data.userName)}</h2>
          
          <p class="course-text">
            for completing the course <span class="course-name">${escapeHtml(data.courseName)}</span>
          </p>
          
          <div class="instructor-section">
            <div class="instructor-name">${instructorName}</div>
            <div class="instructor-label">Course Speaker</div>
          </div>
          
          <div class="details-section">
            <div class="detail-row">
              Issuing Date: <span class="detail-value">${issueDate}</span>
            </div>
            <div class="detail-row">
              Certificate ID: <span class="detail-value">${escapeHtml(data.certificateNumber)}</span>
            </div>
          </div>
        </div>
      </div>
      <script>
        setTimeout(() => window.print(), 500);
      </script>
    </body>
    </html>
  `;
}

/**
 * Opens the printable certificate in a new window.
 * Returns true on success, false when the browser blocked the pop-up — callers
 * must surface that instead of silently doing nothing.
 */
export function openCertificatePrintWindow(data: CertificateData): boolean {
  const printWindow = window.open("", "_blank");
  if (!printWindow) return false;

  printWindow.document.write(generateCertificateHTML(data));
  printWindow.document.close();
  return true;
}
